import { Button } from "../../../components/ui/Button";
import { HubHud } from "../../../components/HubHud";
import type { HubView } from "../../../lib/hubOutput";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { PrincipleButton } from "../../course";

/**
 * FS-03-03 '같이 움직이기' 중 HUD — 장면 전체(리모컨 FS-09 가 아래를 쓴다)라 오른쪽 창 대신 장면 위에:
 * 데려온 친구(별명만) · 기기 힘 게이지(열화 표시) · 내 기계 막힘(빨간 포트) · 내 허브 출력(불빛 · 화면 · 소리 — SR-RUN-03) ·
 * 그만하고 전시관으로 · 원리 보기(P2-S5).
 */
export function PlayHud({
  names,
  degraded,
  gauge,
  stalls,
  onStop,
  principle,
  onPrinciple,
  hub,
}: {
  names: readonly string[];
  degraded: boolean;
  gauge: number;
  stalls: readonly string[];
  onStop: () => void;
  /** P2-S5 원리 보기 토글(없으면 버튼 없음) */
  principle?: boolean;
  onPrinciple?: () => void;
  /** 내 기계 허브 출력(없으면 표시 없음) */
  hub?: { view: HubView; now: number; prime?: boolean };
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  return (
    <>
      <div className="pf-gallery__hud pf-gallery__hud--tl pf-play__who" data-testid="play-friends">
        <span className="pf-hud">
          {t("gallery.friends.playWith", { n: names.length })}
          {names.length > 0 && ` · ${names.join(" · ")}`}
        </span>
      </div>
      <div className="pf-hud-stack">
        <div
          className={`pf-hud pf-play__gauge${degraded ? " pf-play__gauge--warn" : ""}`}
          data-testid="friends-gauge"
          data-degraded={degraded}
        >
          <span>{t("gallery.friends.power")}</span>
          <span
            className={`pf-progress${degraded ? " pf-progress--warn" : ""}`}
            role="meter"
            aria-label={t("gallery.friends.power")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(gauge * 100)}
          >
            <span className="pf-progress__fill" style={{ width: `${Math.round(gauge * 100)}%` }} />
          </span>
          {degraded && (
            <span className="pf-play__degraded" role="status" data-testid="friends-degrade">
              {t("gallery.friends.degradedShort")}
            </span>
          )}
        </div>
        {stalls.map((p) => (
          <div key={p} className="pf-hud pf-hud--stall" role="status" data-testid="hud-stall">
            {t(se ? "course.hud.stallSE" : "course.hud.stallSU", { port: p })}
          </div>
        ))}
        {hub && <HubHud hub={hub.view} now={hub.now} prime={hub.prime} />}
      </div>
      <div className="pf-course__controls" role="group" aria-label={t("gallery.friends.playTitle")}>
        <Button
          variant="ghost"
          icon="🏛"
          onClick={onStop}
          data-testid="friends-stop"
          aria-keyshortcuts="Escape"
        >
          {t("gallery.friends.stop")}
        </Button>
        {onPrinciple && <PrincipleButton on={principle ?? false} onToggle={onPrinciple} />}
      </div>
    </>
  );
}
